import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icon } from '@/components/ui/icon';
import { toast } from "@/hooks/errors/use-toast";
import { supabase } from '@/integrations/supabase/client';
import AuthLayout from '@/components/auth/AuthLayout';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);

  useEffect(() => {
    // PKCE flow: Check for active session created by /auth/confirm endpoint
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          toast({
            title: "Session Error",
            description: "Unable to validate your reset session. Please request a new password reset.",
            variant: "error",
          });
          navigate('/forgot-password');
          return;
        }

        if (session && session.user) {
          // Valid session exists - user can reset password
          console.log('Valid reset session found for user:', session.user.email);
          setIsValidToken(true);
        } else {
          // No session - user needs to go through proper PKCE flow
          toast({
            title: "Invalid Reset Session",
            description: "This password reset session is invalid or has expired. Please request a new password reset.",
            variant: "error",
          });
          navigate('/forgot-password');
        }
      } catch (error) {
        console.error('Error checking reset session:', error);
        toast({
          title: "Error",
          description: "Something went wrong. Please try again.",
          variant: "error",
        });
        navigate('/forgot-password');
      }
    };

    checkSession();
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please make sure both passwords match",
        variant: "error",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters long",
        variant: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Password Updated",
        description: "Your password has been successfully updated. You can now sign in with your new password.",
        variant: "success",
      });

      // Sign out the user and redirect to sign in
      await supabase.auth.signOut();
      navigate('/signin');

    } catch (error: any) {
      console.error('Password update error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password",
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isValidToken) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <AuthLayout
          title="Validating Reset Link..."
          description="Please wait while we validate your password reset link"
        >
          <div className="flex justify-center items-center py-8">
            <Icon name="Loader2" className="h-8 w-8 animate-spin text-accent" />
          </div>
        </AuthLayout>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AuthLayout
        title="Reset Your Password"
        description="Enter your new password below"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-200">
              New Password
            </Label>
            <div className="relative">
              <Input 
                id="password" 
                type={showPassword ? "text" : "password"}
                placeholder="••••••••" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-11 bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 focus-visible:border-accent/40 hover:border-accent/30 hover:shadow-md transition-all duration-300 shadow-sm"
                required
                disabled={isSubmitting}
                minLength={6}
                autoComplete="new-password"
              />
              <Icon 
                name="Lock" 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent" 
                size={18} 
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting}
              >
                <Icon 
                  name={showPassword ? "EyeOff" : "Eye"} 
                  className="text-accent" 
                  size={18} 
                />
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Password must be at least 6 characters long
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Confirm New Password
            </Label>
            <div className="relative">
              <Input 
                id="confirmPassword" 
                type={showConfirmPassword ? "text" : "password"}
                placeholder="••••••••" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 h-11 bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 focus-visible:border-accent/40 hover:border-accent/30 hover:shadow-md transition-all duration-300 shadow-sm"
                required
                disabled={isSubmitting}
                minLength={6}
                autoComplete="new-password"
              />
              <Icon 
                name="Lock" 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent" 
                size={18} 
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isSubmitting}
              >
                <Icon 
                  name={showConfirmPassword ? "EyeOff" : "Eye"} 
                  className="text-accent" 
                  size={18} 
                />
              </Button>
            </div>
          </div>

          <Button 
            type="submit"
            disabled={isSubmitting || !password || !confirmPassword}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-medium py-2.5"
          >
            {isSubmitting ? (
              <>
                <Icon name="Loader2" className="mr-2 h-4 w-4 animate-spin" />
                Updating Password...
              </>
            ) : (
              <>
                Update Password
                <Icon name="Check" className="ml-2" size={16} />
              </>
            )}
          </Button>

          <div className="text-center">
            <Button 
              variant="link" 
              className="p-0 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300" 
              onClick={() => navigate('/signin')}
              type="button"
            >
              ← Back to Sign In
            </Button>
          </div>
        </form>
      </AuthLayout>
    </motion.div>
  );
};

export default ResetPassword; 